/**
 * Shared constants for the SVG import / export pair (Tier 2 Group F
 * sub-pieces #3 + #4). Hosts values that MUST stay in lockstep
 * between the two halves to preserve round-trip correctness.
 *
 * Single-source-of-truth pattern enforces the invariant: the import
 * walker assigns `svgNodeIndex: N` to the Nth element it visits past
 * the skip list; the export walker re-flattens the same tree past
 * the SAME skip list so element N is the same element on both sides.
 * Pre-PR review finding (R2#1) — earlier versions duplicated the
 * list in two files; a divergence would silently corrupt the
 * round trip without any test catching it (the import would emit
 * a layer at index K; the export would index past it and restore
 * the wrong element).
 *
 * Other shared concerns live here too: blend-mode mapping (R2#8)
 * and namespace handling, if those grow in the future.
 */

/** SVG element tags the import walker SKIPS (doesn't emit as a
 *  layer) and the export walker therefore also skips when assigning
 *  flattened indices. Includes declarative + metadata elements
 *  (`<defs>` / `<style>` / `<title>` / `<desc>` / `<metadata>`) and
 *  paint-server / clip definitions (`<linearGradient>` / `<radialGradient>` /
 *  `<pattern>` / `<filter>` / `<mask>` / `<clipPath>` / `<symbol>` /
 *  `<marker>`). Their content lives at document level; the export's
 *  `extractDocLevelDecls` preserves them as siblings of the layer
 *  tree.
 *
 *  Lowercase, frozen. Consumers MUST match on `el.localName.toLowerCase()`
 *  to match SVGs that use namespaced tags. */
export const SVG_WALK_SKIP_TAGS: ReadonlySet<string> = Object.freeze(
  new Set([
    'defs',
    'style',
    'title',
    'desc',
    'metadata',
    'lineargradient',
    'radialgradient',
    'pattern',
    'filter',
    'mask',
    'clippath',
    'symbol',
    'marker',
  ]),
);
